// src/components/operationCenter/widgets/shared/genericWidgetUtils.ts
// Shared utilities for all generic endpoint-based widgets.

import { API_BASE_URL } from "@/services";
import { formatCurrency } from "@/utils/charts/formatters";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type ValueFormat = "number" | "currency" | "percent" | "compact" | "ratio";

export type AnyRecord = Record<string, unknown>;

// ─────────────────────────────────────────────────────────────────────────────
// Fetcher
// ─────────────────────────────────────────────────────────────────────────────

export async function fetchEndpoint(
  endpoint: string,
  params: Record<string, string> = {},
  signal?: AbortSignal,
): Promise<unknown> {
  const base = endpoint.startsWith("http")
    ? endpoint
    : `${API_BASE_URL}${endpoint}`;
  const url = new URL(base);
  Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), signal != null ? { signal } : undefined);
  if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
  return res.json();
}

// ─────────────────────────────────────────────────────────────────────────────
// Data extraction
// ─────────────────────────────────────────────────────────────────────────────

const ARRAY_KEYS = ["data", "results", "items", "series", "records", "rows"];

/** Extract a flat array of objects from various API response shapes. */
export function extractRecords(raw: unknown): AnyRecord[] {
  if (Array.isArray(raw)) return raw as AnyRecord[];
  if (raw && typeof raw === "object") {
    const obj = raw as AnyRecord;
    for (const key of ARRAY_KEYS) {
      if (Array.isArray(obj[key])) return obj[key] as AnyRecord[];
    }
    for (const val of Object.values(obj)) {
      if (Array.isArray(val)) return val as AnyRecord[];
    }
  }
  return [];
}

/** Extract a single object (e.g. { price: 50000, change: 0.02 }). */
export function extractSingle(raw: unknown): AnyRecord {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const obj = raw as AnyRecord;
  // If it has common array wrapper keys, unwrap the first item
  for (const key of ARRAY_KEYS) {
    const val = obj[key];
    if (Array.isArray(val) && val.length > 0) return val[0] as AnyRecord;
  }
  return obj;
}

// ─────────────────────────────────────────────────────────────────────────────
// Param helpers
// ─────────────────────────────────────────────────────────────────────────────

export function parseParams(raw: string): Record<string, string> {
  const result: Record<string, string> = {};
  raw
    .split("&")
    .map((p) => p.trim())
    .filter(Boolean)
    .forEach((pair) => {
      const idx = pair.indexOf("=");
      if (idx === -1) return;
      const k = pair.slice(0, idx).trim();
      const v = pair.slice(idx + 1).trim();
      if (k) result[k] = v;
    });
  return result;
}

export function paramsToString(params: Record<string, string> = {}): string {
  return Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join("&");
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatting
// ─────────────────────────────────────────────────────────────────────────────

export function formatValue(v: unknown, format: ValueFormat = "number"): string {
  const num = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(num)) return String(v ?? "—");

  switch (format) {
    case "currency":
      return formatCurrency(num);
    case "compact": {
      const abs = Math.abs(num);
      if (abs >= 1e12) return `${(num / 1e12).toFixed(2)}T`;
      if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
      return num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    }
    case "percent":
      return `${(num * 100).toFixed(2)}%`;
    case "ratio":
      return `${num.toFixed(2)}x`;
    case "number":
    default: {
      const abs = Math.abs(num);
      if (abs >= 1e9) return `${(num / 1e9).toFixed(2)}B`;
      if (abs >= 1e6) return `${(num / 1e6).toFixed(2)}M`;
      if (abs >= 1e3) return `${(num / 1e3).toFixed(2)}K`;
      return num.toLocaleString("en-US", { maximumFractionDigits: 4 });
    }
  }
}

/** Colour class for a fractional change value (0.03 = +3%). */
export function changeColor(change: number | null | undefined): string {
  if (change == null || !Number.isFinite(change)) return "text-slate-400";
  if (change > 0) return "text-emerald-400";
  if (change < 0) return "text-rose-400";
  return "text-slate-400";
}

/** Format a fractional change value as "+3.00%". */
export function formatChange(change: number | null | undefined): string {
  if (change == null || !Number.isFinite(change)) return "—";
  const sign = change >= 0 ? "+" : "";
  return `${sign}${(change * 100).toFixed(2)}%`;
}
